import type { Options as Schema } from 'rehype-sanitize'

/**
 * Sanitize schema for the Heisig story corpus, which embeds raw
 * `<i>primitive</i>` tags inside markdown prose so the Atlas plate
 * renderer can deep-link italic primitive references.
 *
 * @remarks
 * The whitelist is intentionally narrow: the corpus only carries
 * paragraphs, line breaks, italics, and bold; the primitive description
 * block additionally uses lists. Everything else (script, iframe, event
 * handlers, `javascript:` hrefs, style/srcset attributes) is stripped by
 * hast-util-sanitize before render.
 */
export const storySanitizeSchema: Schema = {
    tagNames: ['p', 'br', 'i', 'em', 'strong', 'b', 'ul', 'ol', 'li'],
    attributes: {},
    protocols: {},
    strip: ['script', 'style'],
}

/**
 * Sanitize schema for community-sourced stories (Kanji Koohii corpus).
 *
 * @remarks
 * Koohii stories embed raw `<strong>`, `<em>`, and `<a>` tags directly
 * in their text, so their renderer must keep raw-HTML parsing enabled —
 * this schema is the defense-in-depth layer that makes that safe.
 *
 * Anchor href is restricted to safe protocols. `target=_blank` +
 * `rel=noopener noreferrer` are applied at render-time in the renderer's
 * `components` map — NOT allowed as input attributes — so koohii input
 * cannot override the security attrs.
 */
export const communitySanitizeSchema: Schema = {
    tagNames: ['p', 'br', 'i', 'em', 'strong', 'b', 'ul', 'ol', 'li', 'a'],
    attributes: {
        a: ['href', 'title'],
    },
    protocols: {
        href: ['http', 'https', 'mailto'],
    },
    strip: ['script', 'style'],
}

/**
 * Sanitize schema for lesson and chapter intro block content.
 *
 * @remarks
 * Intros are authored content under editorial control, so this layer is
 * defense-in-depth in case the content pipeline ever ingests something
 * we don't write ourselves.
 *
 * `<a>` is allowed for cross-references (`[Lesson 7](/journey/lessons/7)`,
 * `[frame 80](/atlas/80)`). Relative paths pass through
 * hast-util-sanitize's protocol allowlist without explicit listing.
 */
export const introSanitizeSchema: Schema = {
    tagNames: ['p', 'br', 'i', 'em', 'strong', 'b', 'ul', 'ol', 'li', 'a'],
    attributes: {
        a: ['href'],
    },
    protocols: {
        href: ['http', 'https', 'mailto'],
    },
    strip: ['script', 'style'],
}
